import { Component, OnInit, ViewChild } from '@angular/core';
import { cloneDeep, indexOf, difference } from 'lodash';
import { Dialog, MessageService, TreeNode } from 'primeng';
import { Message } from '../../classes/Message';
import { ICustomerOrderPreference } from '../../interfaces/organisation-order-preference';
import { IOrganisationTag } from '../../interfaces/organisation-tag';
import { CustomerService } from '../../services';
import { OrganisationOrderPeferenceService } from '../../services';
import { OrganisationTagsService } from '../../services';
import { SupplierService } from '../../services';

@Component({
  selector: 'customer-tagging',
  templateUrl: 'customer-tagging.component.html',
  styleUrls: ['customer-tagging.component.scss'],
})
export class CustomerTaggingComponent implements OnInit {
  public selectedCustomer;

  @ViewChild('customerTaggingDialog') customerTaggingDialog: Dialog;
  public showCustomerTaggingDialog: boolean = false;

  public selectedSuppliers: any[];
  public excludedSuppliers: any[]; // separate list for general exclusions that can be applied to checked items
  public excludeSuppliersReason: string;

  public orgNodes: any[];
  public selectedNode: TreeNode;

  public isLoading: boolean;
  public selectedItems: TreeNode[] = [];
  public lastLength: number = 0;
  public cols: any[];

  // used to apply tag settings to all checked items in the tree
  public generalTags: IOrganisationTag[] = [];

  public showSupplierDialog: boolean = false;
  public isGeneralSupplierDialog: boolean = false;

  tagsLockedMsg =
    'Not all tag operations could be completed as some are locked by Network Assurance';

  constructor(
    private supplierService: SupplierService,
    private customerService: CustomerService,
    private organisationTagsService: OrganisationTagsService,
    private organisationOrderPreferenceService: OrganisationOrderPeferenceService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.setColumns();
  }

  setColumns() {
    this.cols = [
      { field: 'name', header: 'Customer/Department/Team' },
      { field: 'orderPreferences', header: 'Excluded Supplier(s)' },
      { field: 'tags', header: 'Tags' },
    ];
  }

  public showTagging(selectedCustomer) {
    this.showCustomerTaggingDialog = true;
    this.selectedCustomer = selectedCustomer;

    this.loadOrganisationStructure();
    this.customerTaggingDialog.header = `Customer tagging for ${selectedCustomer.name}`;

    window.setTimeout(() => {
      this.customerTaggingDialog.maximize();
    }, 100);
  }

  loadOrganisationStructure() {
    // returns the customer organisation structure along with the tags and exclusions for each dept/team
    // in a format that can be used in the tree table component (i.e. Data and Children in each item)
    this.organisationTagsService
      .getCustomerOrganisationData(this.selectedCustomer.id)
      .subscribe((result) => {
        //console.log("Customer organisation structure loaded: ", result);
        this.orgNodes = result;

        if (this.selectedItems) this.selectedItems.length = 0;

        this.expandNodes(this.orgNodes[0]);
      });
  }

  expandNodes(root: TreeNode) {
    root.expanded = true;
    if (root.children) root.children.forEach((x) => this.expandNodes(x));
  }

  nodeHasTags(rowNode) {
    return rowNode.node.data.tags != null && rowNode.node.data.tags.length > 0;
  }

  nodeHasOrderPreferences(rowNode) {
    return (
      rowNode.node.data.orderPreferences != null && rowNode.node.data.orderPreferences.length > 0
    );
  }

  applyGeneralTags() {
    let promises: Promise<Object>[] = [];

    // save prefs for each selected org/dept/team in the customer tree one at a time
    this.selectedItems.forEach((x) => {
      // create a list of organisation tag records
      var tags = this.generalTags.map((y) => {
        return <IOrganisationTag>{
          organisationId: y.organisationId,
          customerDepartmentId: x.data.departmentId,
          customerTeamId: x.data.teamId,
          tagEntityId: y.tagEntityId,
          tagSet: y.tagSet,
        };
      });

      //console.log("Generated tags list: ", tags);

      let data = cloneDeep(x.data);
      data.tags = tags;

      promises.push(this.organisationTagsService.saveCustomerTags(data));
    });

    Promise.all(promises).then((results) => {
      this.loadOrganisationStructure();
    });
  }

  removeGeneralTags() {
    this.selectedItems.forEach((x) => {
      let data = cloneDeep(x.data);
      data.tags.length = 0;

      this.organisationTagsService.saveCustomerTags(data).then((result) => {
        if (!result) {
          this.messageService.add(Message.Error(this.tagsLockedMsg, 'Tags Locked'));
        }
        this.loadOrganisationStructure();
      });
    });
  }

  selectSupplierExclusions() {
    this.isGeneralSupplierDialog = true;
    this.selectedSuppliers = cloneDeep(this.excludedSuppliers);

    // set the selected suppliers in the component

    this.showSupplierDialog = true;
  }

  onSelectedSuppliersChanged(selected: any[]) {
    this.selectedSuppliers = selected;
  }

  showSuppliersForOrderPreference(rowNode) {
    this.selectedNode = rowNode.node;
    this.excludeSuppliersReason = rowNode.node.data.orderPreferences[0].reason;

    this.selectedSuppliers = rowNode.node.data.orderPreferences.map((y) => {
      return y.targetOrganisation;
    });

    this.showSupplierDialog = true;
  }

  deleteOrderPreferences(rowNode) {
    // clear the orderPreferences array from the node and pass it to the service to update
    rowNode.node.data.orderPreferences.length = 0;

    this.organisationOrderPreferenceService
      .saveCustomerOrderPreferences(rowNode.node.data)
      .then((result) => {
        if (result) {
          // todo: show message that prefs are deleted
        }
      });
  }

  onSupplierExclusionDialogClosed() {
    if (this.isGeneralSupplierDialog) {
      this.excludedSuppliers = cloneDeep(this.selectedSuppliers);
    } else {
      // add the excluded suppliers to the selected org/dept/team (will be replaced if already exists)
      var prefs = this.selectedSuppliers.map((y) => {
        return <ICustomerOrderPreference>{
          organisationId: this.selectedCustomer.id,
          customerDepartmentId: this.selectedNode.data.departmentId,
          customerTeamId: this.selectedNode.data.teamId,
          targetOrganisationId: y.id,
          reason: this.excludeSuppliersReason,
        };
      });

      let data = cloneDeep(this.selectedNode.data);
      data.orderPreferences = prefs;

      this.organisationOrderPreferenceService.saveCustomerOrderPreferences(data);
    }

    this.isGeneralSupplierDialog = false;
    this.loadOrganisationStructure();
  }

  applyGeneralExclusions() {
    // ensure there is a reason for the exclusion
    if (
      !this.excludeSuppliersReason ||
      (this.excludeSuppliersReason && this.excludeSuppliersReason.trim() == '')
    ) {
      this.messageService.clear();
      this.messageService.add(
        Message.Error(
          'Please add a reason for the exclusion in the supplied text area.',
          'No reason given for exclusion',
        ),
      );
      return;
    }

    let promises: Promise<any>[] = [];

    // save prefs for each selected org/dept/team in the customer tree one at a time
    this.selectedItems.forEach((x) => {
      // create a list of order preference records, one for each supplier in the exclusion list
      var prefs = this.excludedSuppliers.map((y) => {
        return <ICustomerOrderPreference>{
          organisationId: this.selectedCustomer.id,
          customerDepartmentId: x.data.departmentId,
          customerTeamId: x.data.teamId,
          targetOrganisationId: y.id,
          reason: this.excludeSuppliersReason,
        };
      });

      let data = cloneDeep(x.data);
      data.orderPreferences = prefs;

      promises.push(this.organisationOrderPreferenceService.saveCustomerOrderPreferences(data));
    });

    Promise.all(promises).then((results) => {
      this.loadOrganisationStructure();
    });
  }

  removeGeneralExclusions() {
    this.selectedItems.forEach((x) => {
      x.data.orderPreferences.length = 0;

      this.organisationOrderPreferenceService
        .saveCustomerOrderPreferences(x.data)
        .then((result) => {
          if (result) {
            // todo: show message that prefs are deleted
          }
        });
    });
  }

  parentChecked(rowNode: any): boolean {
    let parentChecked =
      (rowNode.node.parent != null && indexOf(this.selectedItems, rowNode.node.parent) >= 0) ||
      (rowNode.node.parent != null &&
        rowNode.node.parent.parent != null &&
        indexOf(this.selectedItems, rowNode.node.parent.parent) >= 0);

    return parentChecked;
  }

  isRowSelected(rowNode: any): boolean {
    return this.selectedItems && indexOf(this.selectedItems, rowNode.node) >= 0;
  }

  toggleRowSelection(rowNode: any) {
    if (this.parentChecked(rowNode)) return;

    if (this.isRowSelected(rowNode)) {
      this.selectedItems.splice(this.selectedItems.indexOf(rowNode.node), 1);
    } else {
      this.selectedItems.push(rowNode.node);

      // if the node has children, remove them from the selection array
      this.removeChildrenFromSelectedItems(rowNode.node);
    }

    this.selectedItems = [...this.selectedItems];
  }

  removeChildrenFromSelectedItems(node: TreeNode) {
    if (node.children && node.children.length > 0) {
      this.selectedItems = difference(this.selectedItems, node.children);

      node.children.forEach((x) => {
        this.removeChildrenFromSelectedItems(x);
      });
    }
  }

  onTagSelectorClosed(tagData, isGeneral: boolean, rowNode?: any) {
    //console.log("Tag selection for organisation: ", tagData)

    // if this is for the general tag component, save into the general array.
    // this can then be applied to all checked items in the tree as required
    if (isGeneral == true) {
      this.generalTags = this.cloneAndSetupTags(tagData, this.generalTags);

      console.log('General tags: ', this.generalTags);
    } else {
      let data = cloneDeep(rowNode.node.data);
      data.tags = this.cloneAndSetupTags(tagData, data.tags, rowNode);

      this.organisationTagsService.saveCustomerTags(data).then((result) => {
        if (!result) {
          this.messageService.add(Message.Error(this.tagsLockedMsg, 'Tags Locked'));
        }

        this.loadOrganisationStructure();
      });
    }
  }

  cloneAndSetupTags(
    tagData: any,
    tagArray: IOrganisationTag[],
    nodeData?: any,
  ): IOrganisationTag[] {
    console.log('Tag data: ', tagData);

    tagArray = cloneDeep(tagArray);

    // remove all tags from the current tagSet
    tagArray = tagArray.filter((x) => x.tagSet != tagData.tagSet);

    // re-add tags for the current tagSet
    tagData.tags.forEach((x) => {
      tagArray.push(<IOrganisationTag>{
        organisationId: this.selectedCustomer.id,
        customerDepartmentId: nodeData ? nodeData.departmentId : null,
        customerTeamId: nodeData ? nodeData.teamId : null,
        tagEntityId: x.id,
        tagSet: tagData.tagSet,
        tagEntity: x,
      });
    });

    return tagArray;
  }
}
